import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { FileText, FileSpreadsheet, RotateCcw, Eye } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const RKEY = (uid) => `ops_reports_${uid || 'anon'}`;

const OperationsReports = () => {
  const { token, user } = useContext(AuthContext);
  const [meta, setMeta] = useState(null);
  const [source, setSource] = useState('');
  const [detail, setDetail] = useState('detailed');
  const [records, setRecords] = useState('all');
  const [search, setSearch] = useState('');
  const [fields, setFields] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restored, setRestored] = useState(false);

  // Load meta + restore persisted state.
  useEffect(() => {
    axios.get(`${API}/operations/reports/meta`, auth(token)).then((r) => {
      setMeta(r.data);
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(RKEY(user?.id))); } catch { /* noop */ }
      const first = r.data.sources[0];
      if (saved && r.data.sources.some((s) => s.key === saved.source)) {
        setSource(saved.source); setDetail(saved.detail || 'detailed');
        setRecords(saved.records || 'all'); setSearch(saved.search || '');
        setFields(saved.fields || (r.data.sources.find((s) => s.key === saved.source)?.default_fields || []));
      } else if (first) {
        setSource(first.key); setFields(first.default_fields);
      }
      setRestored(true);
    }).catch(() => toast.error('Unable to load report options.'));
  }, [token]);

  const srcDef = meta?.sources.find((s) => s.key === source);

  // Persist whenever the config changes.
  useEffect(() => {
    if (!restored || !source) return;
    try { localStorage.setItem(RKEY(user?.id), JSON.stringify({ source, detail, records, search, fields })); } catch { /* noop */ }
  }, [source, detail, records, search, fields, restored, user]);

  const onSource = (key) => {
    setSource(key);
    setFields(meta.sources.find((s) => s.key === key)?.default_fields || []);
    setPreview(null);
  };
  const toggleField = (fk) => setFields((p) => (p.includes(fk) ? p.filter((x) => x !== fk) : [...p, fk]));

  const body = () => ({ source, detail, fields, records, search: records === 'filtered' ? search : '' });

  const runPreview = async () => {
    if (!source) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/operations/reports/preview`, body(), auth(token));
      setPreview(r.data);
    } catch { toast.error('Preview failed.'); } finally { setLoading(false); }
  };

  const exportAs = async (fmt) => {
    if (!source) return;
    try {
      const r = await axios.post(`${API}/operations/reports/export`, { ...body(), fmt }, { ...auth(token), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `operations_${source}.${fmt === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed.'); }
  };

  const reset = () => {
    try { localStorage.removeItem(RKEY(user?.id)); } catch { /* noop */ }
    const first = meta?.sources[0];
    if (first) { setSource(first.key); setFields(first.default_fields); }
    setDetail('detailed'); setRecords('all'); setSearch(''); setPreview(null);
    toast.success('Report reset to defaults.');
  };

  return (
    <div data-testid="ops-reports">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">Operations Module</div>
          <h1 className="mt-1.5 text-[30px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">Reports</h1>
          <p className="mt-2 max-w-2xl text-base text-slate-500">
            The established ecosystem report-builder. Sources are added as each module becomes operational —
            this build ships Inspections and Corrective Actions.
          </p>
        </div>
        <button onClick={reset} data-testid="ops-report-reset" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          <RotateCcw size={15} /> Reset Report
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-report-controls">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Report Source</label>
            <select data-testid="ops-report-source" value={source} onChange={(e) => onSource(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#8FC93A]">
              {meta?.sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Detail Level</label>
            <select data-testid="ops-report-detail" value={detail} onChange={(e) => setDetail(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#8FC93A]">
              {meta?.detail_levels.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Records</label>
            <div className="flex gap-2">
              {[['all', 'All'], ['filtered', 'Filtered']].map(([k, l]) => (
                <button key={k} data-testid={`ops-report-records-${k}`} onClick={() => setRecords(k)}
                  className={`flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium ${records === k ? 'border-[#8FC93A] bg-[#8FC93A]/10 text-[#4d7c0f]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
            </div>
            {records === 'filtered' && (
              <input data-testid="ops-report-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter text…"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#8FC93A]" />
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Include Fields</label>
            <div className="space-y-1.5">
              {srcDef?.fields.map((fld) => (
                <label key={fld.key} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600" data-testid={`ops-report-field-${fld.key}`}>
                  <input type="checkbox" checked={fields.includes(fld.key)} onChange={() => toggleField(fld.key)} className="h-4 w-4 accent-[#8FC93A]" />
                  {fld.label}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2 pt-1">
            <button onClick={runPreview} disabled={loading} data-testid="ops-report-preview-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0E2A47] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#0E2A47]/90 disabled:opacity-50">
              <Eye size={15} /> {loading ? 'Building…' : 'Preview'}
            </button>
            <div className="flex gap-2">
              <button onClick={() => exportAs('pdf')} data-testid="ops-report-pdf" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50"><FileText size={15} /> PDF</button>
              <button onClick={() => exportAs('xlsx')} data-testid="ops-report-xlsx" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50"><FileSpreadsheet size={15} /> Excel</button>
            </div>
          </div>
        </div>

        <div className="min-w-0 rounded-xl border border-slate-200 bg-white" data-testid="ops-report-preview">
          {!preview ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-slate-400">
              <Eye size={30} className="mb-2 text-slate-300" />
              <p className="text-sm">Choose a source and fields, then press <span className="font-semibold text-slate-500">Preview</span>.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                <h3 className="text-sm font-bold text-[#0E2A47]">{preview.title}</h3>
                <span className="text-xs text-slate-400">{preview.count} record(s) · {preview.generated_at}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>{preview.columns.map((c, i) => <th key={i} className="px-4 py-3 whitespace-nowrap">{c}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.rows.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        {r.cells.map((v, i) => <td key={i} className="px-4 py-2.5 text-slate-600">{v}</td>)}
                      </tr>
                    ))}
                    {preview.rows.length === 0 && <tr><td colSpan={preview.columns.length} className="px-4 py-10 text-center text-slate-400">No records match.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationsReports;
